import type { AdminRouterContext } from '@/lib/router-context'
import type {
  SalesOrderIndexResponse,
  SalesOrderListItem,
} from '@/lib/services/sales-orders/sales-orders.types'
import type { SalesOrderSearchRecord } from '@/lib/services/sales-orders/sales-order.schema'
import { readProperty } from '@/lib/utils'
import { CondolencesReconnectNotice } from '@/components/integrations/condolences/CondolencesReconnectNotice'
import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import CreateActionButton from '@/components/patterns/create-action-button'
import { OrganizationContextNotice } from '@/components/patterns/info-notice'
import OrderHeader, { OrderFilterBar, OrderSourceTabs } from '@/components/orders/OrderHeader'
import { OrdersDayView } from '@/components/orders/OrdersDayView'
import { OrdersMonthView } from '@/components/orders/OrdersMonthView'
import { SalesOrdersCalendarMonthPicker } from '@/components/orders/SalesOrdersCalendarMonthPicker'
import { SalesOrdersIndexTable } from '@/components/orders/SalesOrdersIndexTable'
import { buildSalesOrderExportSheets } from '@/components/orders/sales-order-export'
import { CashDrawerMenu } from '@/components/pos/CashDrawerMenu'
import { RecoverablePosCartBanner } from '@/components/pos/RecoverablePosCartBanner'
import { SavedPosCartsLink } from '@/components/pos/SavedPosCartsLink'
import { getIndexPaginationMeta, IndexTableExportButton } from '@/components/tables/IndexTable'
import {
  fetchAllIndexTablePages,
  fetchIndexTableQueryPage,
  useIndexTableSpreadsheetExport,
} from '@/components/tables/indexTableExport'
import { useIndexTableSearchParams } from '@/components/tables/useIndexTableSearchParams'
import useIsGlobalOrg from '@/hooks/useIsGlobalOrg'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can, useAbility } from '@/lib/casl'
import { canManageSalesOrderField } from '@/lib/sales-order-access'
import { OrganizationType } from '@/lib/enums/OrganizationType'
import queryClient from '@/lib/queryClient'
import {
  getSalesOrderCalendarDateRange,
  getSalesOrderCalendarSummaryFilters,
  getSalesOrderLayoutChangeParams,
  getSalesOrderNavigationSearchParams,
  getSalesOrderUrlSearchParams,
} from '@/lib/services/sales-orders/sales-order.index-state'
import {
  SALES_ORDER_SOURCE_FILTERS,
  getCurrentSalesOrderCalendarMonth,
  getImplicitSalesOrderFilterKeys,
  getSalesOrderCalendarMonth,
  getSalesOrderSearchParams,
  isSalesOrderUserAssignmentFilter,
  salesOrderUrlSearchSchema,
  stripSalesOrderSearchParams,
} from '@/lib/services/sales-orders/sales-order.schema'
import {
  salesOrderQueries,
  useInfiniteSalesOrders,
  useSalesOrderCalendarSummary,
} from '@/lib/services/sales-orders/sales-orders.query'
import { userQueries } from '@/lib/services/users/users.query'
import { canAccessUsersInOrganization } from '@/lib/user-access'
import { useQuery } from '@tanstack/react-query'
import {
  createFileRoute,
  Link,
  notFound,
  useNavigate,
  type SearchSchemaInput,
} from '@tanstack/react-router'
import { ShoppingCart } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

export const Route = createFileRoute('/_authenticated/$orgId/sales-orders/')({
  component: SalesOrderIndex,
  validateSearch: (search: SalesOrderSearchRecord & SearchSchemaInput) =>
    salesOrderUrlSearchSchema.parse(search),
  search: {
    middlewares: [stripSalesOrderSearchParams],
  },
  loaderDeps: ({ search }) => ({ search }),
  loader: salesOrderIndexLoader,
})

export function salesOrderIndexLoader({
  context,
  deps: { search },
  params,
}: {
  context: AdminRouterContext
  deps: { search: SalesOrderSearchRecord }
  params: { orgId: number | string }
}): { isReadOnly: boolean } {
  if (!context.ability.can('list', 'sales-orders')) throw notFound()

  const organizationForUserAccess = {
    type:
      Number(params.orgId) === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)
        ? OrganizationType.Global.Value
        : OrganizationType.Florist.Value,
  }
  const isGlobalOrg = organizationForUserAccess.type === OrganizationType.Global.Value
  const filters = getSalesOrderIndexFilters(search, isGlobalOrg)
  const indexFilters = { ...filters, include_meta: true }
  const isGlobalCalendar =
    search.layout === 'calendar' &&
    context.ability.can('access', 'global') &&
    Number(params.orgId) === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)

  if (search.layout === 'grid') {
    void context.queryClient.prefetchInfiniteQuery?.(
      salesOrderQueries.infinite({
        organizationId: params.orgId,
        filters: indexFilters,
        pageSize: filters.page_size,
      })
    )
  } else if (isGlobalCalendar) {
    const calendarDateRange = getSalesOrderCalendarDateRange(search)

    void context.queryClient.prefetchQuery(
      salesOrderQueries.calendarSummary({
        organizationId: params.orgId,
        filters: getSalesOrderCalendarSummaryFilters(filters, calendarDateRange),
      })
    )
  } else {
    void context.queryClient.prefetchQuery(
      salesOrderQueries.index({ organizationId: params.orgId, filters: indexFilters })
    )
  }
  if (!isGlobalOrg) {
    void context.queryClient.prefetchQuery(
      salesOrderQueries.assignees({ organizationId: params.orgId })
    )
  }
  if (canAccessUsersInOrganization(context.ability, organizationForUserAccess)) {
    void context.queryClient.prefetchQuery(userQueries.index({ organizationId: params.orgId }))
  }

  const canEditSalesOrderDetails = canManageSalesOrderField(context.ability, 'details')

  return { isReadOnly: !canEditSalesOrderDetails }
}

function getSalesOrdersFromResponse(
  response: SalesOrderIndexResponse | undefined
): SalesOrderListItem[] {
  return Array.isArray(response) ? response : (response?.data ?? [])
}

function getAvailableSourceFiltersFromResponse(
  response: SalesOrderIndexResponse | undefined
): string[] | null {
  return Array.isArray(response) ? null : (response?.meta?.available_source_filters ?? null)
}

function getHasAssignedOrdersFromResponse(response: SalesOrderIndexResponse | undefined): boolean {
  return Array.isArray(response) ? false : Boolean(response?.meta?.has_assigned_orders)
}

export function getSalesOrderIndexFilters(
  search: SalesOrderSearchRecord,
  isGlobalOrg: boolean
): SalesOrderSearchRecord {
  const filters = getSalesOrderSearchParams(search)

  if (isGlobalOrg && isSalesOrderUserAssignmentFilter(filters.assignment_filter)) {
    return { ...filters, assignment_filter: 'all' }
  }

  return filters
}

export function SalesOrderIndex() {
  const path = usePathHelper()
  const navigate = useNavigate()
  const { orgId } = Route.useParams()
  const ability = useAbility()
  const isGlobalOrg = useIsGlobalOrg()
  const showOrganization = ability.can('access', 'global') && isGlobalOrg
  const rawUrlSearchParams = Route.useSearch()
  const urlSearchParams = getSalesOrderUrlSearchParams(rawUrlSearchParams)
  const filters = getSalesOrderIndexFilters(rawUrlSearchParams, isGlobalOrg)
  const indexFilters = { ...filters, include_meta: true }
  const implicitFilterKeys = getImplicitSalesOrderFilterKeys(rawUrlSearchParams)
  const { layout } = urlSearchParams
  const search = urlSearchParams.search ?? ''
  const isGridLayout = layout === 'grid'
  const isCalendarLayout = layout === 'calendar'
  const isGlobalCalendarLayout = isCalendarLayout && showOrganization
  const calendarMonth =
    layout === 'calendar' ? getSalesOrderCalendarMonth(urlSearchParams) : undefined
  const calendarDateRange =
    layout === 'calendar' ? getSalesOrderCalendarDateRange(urlSearchParams) : filters.due_range
  const { updatePage, updatePageSize, updateSearchTerm, updateSort, updateTableParams } =
    useIndexTableSearchParams({
      navigate,
      getSearchParams: getSalesOrderNavigationSearchParams,
      searchTerm: search,
    })

  const gridLoadMoreRef = useRef<HTMLDivElement | null>(null)
  const {
    data: salesOrdersResponse,
    isFetching,
    isPending,
  } = useQuery({
    ...salesOrderQueries.index({ organizationId: orgId, filters: indexFilters }),
    enabled: layout === 'list' || (isCalendarLayout && !isGlobalCalendarLayout),
    refetchInterval: 1000 * 60,
  })
  const {
    data: pagedSalesOrdersResponse,
    fetchNextPage: fetchNextGridPage,
    hasNextPage: hasNextGridPage = false,
    isFetching: isFetchingGrid,
    isFetchingNextPage: isFetchingNextGridPage,
    isPending: isPendingGrid,
  } = useInfiniteSalesOrders({
    organizationId: orgId,
    filters: indexFilters,
    pageSize: filters.page_size,
    enabled: isGridLayout,
    refetchInterval: isGridLayout ? 1000 * 60 : false,
  })
  const {
    data: calendarSummary = [],
    isFetching: isFetchingCalendarSummary,
    isPending: isPendingCalendarSummary,
  } = useSalesOrderCalendarSummary({
    organizationId: orgId,
    filters: getSalesOrderCalendarSummaryFilters(filters, calendarDateRange),
    enabled: isGlobalCalendarLayout,
    refetchInterval: isGlobalCalendarLayout ? 1000 * 60 : false,
  })
  const pagedSalesOrderPages = pagedSalesOrdersResponse?.pages ?? []
  const salesOrderSourceFilterResponse = isGridLayout
    ? pagedSalesOrderPages[0]
    : salesOrdersResponse
  const availableSourceFilters = getAvailableSourceFiltersFromResponse(
    salesOrderSourceFilterResponse
  )
  const showAssigneeColumn =
    getHasAssignedOrdersFromResponse(salesOrderSourceFilterResponse) ||
    Boolean(filters.assignment_filter && filters.assignment_filter !== 'all')
  const availableSourceFiltersKey = availableSourceFilters?.join('|') ?? ''
  const pagedSalesOrders = pagedSalesOrderPages.flatMap(getSalesOrdersFromResponse)
  const salesOrders = isGridLayout
    ? pagedSalesOrders
    : getSalesOrdersFromResponse(salesOrdersResponse)
  const pagedSalesOrdersTotal = Number(
    readProperty(readProperty(pagedSalesOrderPages[0], 'meta'), 'total') ?? salesOrders.length
  )
  const calendarSummaryTotal = calendarSummary.reduce(
    (total, summaryItem) => total + Number(summaryItem.count ?? 0),
    0
  )
  const paginationMeta = getIndexPaginationMeta(
    salesOrdersResponse,
    urlSearchParams.page,
    urlSearchParams.page_size
  )
  const { exportDisabled, exportPending, handleExport } = useIndexTableSpreadsheetExport({
    columns: [],
    rowCount: paginationMeta.total,
    filename: 'sales-orders.xlsx',
    sheetName: 'Orders',
    buildSheets: (orders: SalesOrderListItem[]) =>
      buildSalesOrderExportSheets({
        orders,
        showOrganization,
      }),
    loadRows: ({ signal }: { signal?: AbortSignal } = {}) =>
      fetchAllIndexTablePages({
        signal,
        fetchPage: ({ page, pageSize, signal }) =>
          fetchIndexTableQueryPage(
            salesOrderQueries.index({
              organizationId: orgId,
              filters: {
                ...indexFilters,
                page,
                page_size: pageSize,
              },
            }),
            { signal }
          ),
      }),
  })
  useEffect(() => {
    if (
      !isGridLayout ||
      !hasNextGridPage ||
      isFetchingNextGridPage ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return
    }

    const loadMoreElement = gridLoadMoreRef.current

    if (!loadMoreElement) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void fetchNextGridPage()
        }
      },
      { rootMargin: '320px 0px' }
    )

    observer.observe(loadMoreElement)

    return () => observer.disconnect()
  }, [fetchNextGridPage, hasNextGridPage, isFetchingNextGridPage, isGridLayout])

  useEffect(() => {
    if (!Array.isArray(availableSourceFilters) || availableSourceFilters.length === 0) {
      return
    }

    const selectedSourceFilter = filters.source_filter ?? SALES_ORDER_SOURCE_FILTERS.All

    if (
      selectedSourceFilter !== SALES_ORDER_SOURCE_FILTERS.All &&
      !availableSourceFilters.includes(selectedSourceFilter)
    ) {
      updateTableParams({
        source_filter: undefined,
        filter: undefined,
      })
    }
  }, [availableSourceFilters, availableSourceFiltersKey, filters.source_filter, updateTableParams])

  const handleFilterChange = (key: string, value: string) =>
    updateTableParams({
      [key]:
        value === 'all' && !(layout === 'grid' && key === 'fulfillment_status_filter')
          ? undefined
          : value,
      filter: undefined,
    })
  const handleLayoutChange = (nextLayout: string) =>
    updateTableParams(getSalesOrderLayoutChangeParams(rawUrlSearchParams, nextLayout))
  const handleClearFilters = () =>
    updateTableParams({
      search: undefined,
      due: undefined,
      due_range: undefined,
      created: undefined,
      created_range: undefined,
      customerId: undefined,
      month: layout === 'calendar' ? undefined : urlSearchParams.month,
      filter: layout === 'grid' ? 'all' : undefined,
      payment_filter: undefined,
      fulfillment_type_filter: undefined,
      fulfillment_status_filter: layout === 'grid' ? 'all' : undefined,
      assignment_filter: undefined,
      ...(layout === 'grid' ? { sort: undefined } : {}),
    })
  const handleRemoveFilter = (key: string) => updateTableParams({ [key]: undefined })
  const handleCalendarMonthChange = (month: string) =>
    updateTableParams({
      month: month === getCurrentSalesOrderCalendarMonth() ? undefined : month,
      due: undefined,
      due_range: undefined,
      created: undefined,
      created_range: undefined,
      sort: undefined,
    })
  const handlePageIntent = (page: number) => {
    void queryClient.prefetchQuery(
      salesOrderQueries.index({
        organizationId: orgId,
        filters: getSalesOrderIndexFilters({ ...urlSearchParams, page }, isGlobalOrg),
      })
    )
  }
  const calendarControls =
    layout === 'calendar' ? (
      <SalesOrdersCalendarMonthPicker
        month={calendarMonth}
        onMonthChange={handleCalendarMonthChange}
      />
    ) : null

  const [showAlert, setShowAlert] = useState(true)

  return (
    <div className="space-y-4">
      <IfGlobalOrg>
        {showAlert && (
          <OrganizationContextNotice className="mb-4" onDismiss={() => setShowAlert(false)}>
            You are currently viewing orders for all organizations.
          </OrganizationContextNotice>
        )}
      </IfGlobalOrg>

      <AdminPageHeader
        icon={ShoppingCart}
        title="Orders"
        actions={
          <IfGlobalOrg show={false}>
            <CashDrawerMenu />
            <Can do="create" on="sales-orders">
              <SavedPosCartsLink orgId={orgId} />
              <CreateActionButton asChild>
                <Link {...path.salesOrders.link('create')}>Create Order</Link>
              </CreateActionButton>
            </Can>
          </IfGlobalOrg>
        }
      />

      <IfGlobalOrg show={false}>
        <CondolencesReconnectNotice className="mb-4" />
        <Can do="create" on="sales-orders">
          <RecoverablePosCartBanner currentCartId={null} orgId={orgId} />
        </Can>
      </IfGlobalOrg>

      <div className="space-y-4">
        <div className="space-y-3">
          <OrderSourceTabs
            value={filters.source_filter}
            availableSourceFilters={availableSourceFilters}
            onValueChange={handleFilterChange}
          />
          <OrderFilterBar
            calendarControls={calendarControls}
            filters={filters}
            implicitFilterKeys={implicitFilterKeys}
            layout={layout}
            search={search}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            onRemoveFilter={handleRemoveFilter}
            showAssigneeOptions={!isGlobalOrg}
          />
        </div>

        {layout === 'list' ? (
          <SalesOrdersIndexTable
            orders={salesOrders}
            response={salesOrdersResponse}
            page={urlSearchParams.page}
            pageSize={urlSearchParams.page_size}
            sort={urlSearchParams.sort}
            isLoading={isPending}
            showAssignee={showAssigneeColumn}
            toolbar={
              <OrderHeader
                layout={layout}
                search={search}
                numResults={paginationMeta.total}
                isFetching={isFetching}
                actions={
                  <IndexTableExportButton
                    disabled={exportDisabled}
                    isPending={exportPending}
                    onClick={handleExport}
                  />
                }
                onLayoutChange={handleLayoutChange}
                onSearchCommit={updateSearchTerm}
              />
            }
            onSortChange={updateSort}
            onPageChange={updatePage}
            onPageIntent={handlePageIntent}
            onPageSizeChange={updatePageSize}
          />
        ) : (
          <>
            <div className="overflow-hidden rounded-md border border-border bg-card">
              <OrderHeader
                layout={layout}
                search={search}
                numResults={
                  isGridLayout
                    ? pagedSalesOrdersTotal
                    : isGlobalCalendarLayout
                      ? calendarSummaryTotal
                      : salesOrders.length
                }
                isFetching={
                  isGridLayout
                    ? isFetchingGrid
                    : isGlobalCalendarLayout
                      ? isFetchingCalendarSummary
                      : isFetching
                }
                onLayoutChange={handleLayoutChange}
                onSearchCommit={updateSearchTerm}
              />
            </div>

            {layout === 'grid' && (
              <OrdersDayView
                orders={salesOrders}
                hasNextPage={hasNextGridPage}
                isFetchingNextPage={isFetchingNextGridPage}
                isLoading={isPendingGrid}
                loadMoreRef={gridLoadMoreRef}
              />
            )}
            {layout === 'calendar' && (
              <OrdersMonthView
                orderCounts={isGlobalCalendarLayout ? calendarSummary : []}
                orders={isGlobalCalendarLayout ? [] : salesOrders}
                dateRange={calendarDateRange}
                month={calendarMonth}
                isLoading={isGlobalCalendarLayout ? isPendingCalendarSummary : isPending}
                showOrderCounts={isGlobalCalendarLayout}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}
